import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppModal from '../AppModal';
import { getStoreNumber, setStoreNumber } from '../../utils/storeNumber';
import './index.scss';

// Gates the app behind a store number when ?instore=true or ?instore=1 is present, so
// kiosk/in-store devices can be attributed to a location. The flag is a one-way
// provisioning trigger: once a number is saved it is kept in localStorage indefinitely
// and the badge stays visible on every route, since an in-store device is never used
// for anything else. Navigating without ?instore does not unset it.
const StoreNumberGate = () => {
  const location = useLocation();
  const instoreParam = new URLSearchParams(location.search).get('instore');
  const isInStore = instoreParam === 'true' || instoreParam === '1';

  const [storeNumber, setStoreNumberState] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const existing = getStoreNumber();
    setStoreNumberState(existing);
    setIsModalOpen(isInStore && !existing);
  }, [isInStore]);

  const openEditor = useCallback(() => {
    setInputValue(storeNumber || '');
    setError('');
    setIsModalOpen(true);
  }, [storeNumber]);

  // No-op when there's no saved number yet, so overlay-click/Esc can't dismiss
  // the modal before a store number has been entered.
  const closeEditor = useCallback(() => {
    if (!storeNumber) return;
    setIsModalOpen(false);
  }, [storeNumber]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed) {
        setError('Please enter a store number.');
        return;
      }
      setStoreNumber(trimmed);
      setStoreNumberState(trimmed);
      setIsModalOpen(false);
    },
    [inputValue]
  );

  if (!isInStore && !storeNumber) return null;

  return (
    <>
      {storeNumber && (
        <button
          type="button"
          className="store-number-badge"
          onClick={openEditor}
          aria-label={`Store number ${storeNumber}, click to edit`}
        >
          <i className="fa-sharp fa fa-store store-number-badge-icon" aria-hidden="true" />
          <span className="store-number-badge-label">Store #{storeNumber}</span>
          <i className="fa-sharp fa fa-pen store-number-badge-edit" aria-hidden="true" />
        </button>
      )}
      <AppModal
        isOpen={isModalOpen}
        onClose={closeEditor}
        hideCloseIcon={!storeNumber}
        size="xs"
        overlayClassName="store-number-overlay"
      >
        <form onSubmit={handleSubmit} className="store-number-form">
          <i className="fa-sharp fa fa-store store-number-form-icon" aria-hidden="true" />
          <h2 className="store-number-form-title">Enter Store Number</h2>
          <p className="store-number-form-subtitle">
            This device is set up for in-store use. Enter your store number to continue.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError('');
            }}
            placeholder="e.g. 1234"
            className="store-number-form-input"
          />
          {error && <p className="store-number-form-error">{error}</p>}
          <button type="submit" className="store-number-form-submit">
            Save
          </button>
        </form>
      </AppModal>
    </>
  );
};

export default StoreNumberGate;
